import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';
import { SpinnerComponent } from 'src/app/modules/shared/components/spinner/spinner.component';
import { Organization, RoleGroup } from 'src/app/modules/user-profile/models/role-request.models';
import { RoleRequestService } from 'src/app/modules/user-profile/services/role-request.service';

@Component({
    selector: 'app-multi-org-filter-ta',
    imports: [CommonModule, FormsModule, NgSelectModule, NgxSpinnerModule, SpinnerComponent],
    templateUrl: './multi-org-filter-ta.component.html',
    styleUrl: './multi-org-filter-ta.component.css'
})
export class MultiOrgFilterTAComponent implements OnInit {
  private roleRequestService = inject(RoleRequestService);
  private spinner = inject(NgxSpinnerService);


  readonly showSelectRoles = input<boolean>(undefined);
  readonly showRequired = input<boolean>(undefined);
  readonly isRoleAdmin = input<boolean>(undefined);

  readonly onOrgChange = output<Organization>();
  readonly onRoleGroupChange = output<RoleGroup>();

  @Input() pageType: string;
  
  orgDisable = false;
  showOrg = true;
  labelUp = false;
  selectedOrganization: Organization;
  searchSubject: Subject<string> = new Subject<string>();
  selectedRoleGroup: RoleGroup;
  organizations: Organization[] = [];
  roleGroups: RoleGroup[] = [];
  hasProfileAdmin = false;
  profileAdminCheckbox = 2123430000;
  initialOrg: Organization;
  
  ngOnInit(): void {
    this.spinner.show("multi-org-ta");
    this.roleRequestService.hasAuthorizationForComponent(this.profileAdminCheckbox).subscribe((res: boolean) => {
      this.hasProfileAdmin = res;
      this.getRoleGroups();
      this.watchForSearchTermChange();
    });
  }

  watchForSearchTermChange() {
    this.searchSubject.pipe(
      debounceTime(300),  // Wait 300ms after the last event
      distinctUntilChanged(),  // Only continue if search term changes
      switchMap((term: string) => {
        // If term is at least 3 characters, make the API request
        if (term.length >= 3) {
          return this.roleRequestService.getOrganizationsRolesByAssignGroup(this.selectedRoleGroup.roleAssignGroupKey, term, (this.hasProfileAdmin || this.pageType == 'add-role')? 0: this.initialOrg?.orgKey );
        } else {
          return []; // No results if less than 3 characters
        } 
      })
    ).subscribe((organizations: Organization[]) => {
      this.organizations = organizations;
      if(this.initialOrg) {
        this.organizations.push(this.initialOrg);
      }
      this.spinner.hide("multi-org-ta")
    }, err => {
      this.spinner.hide("multi-org-ta")
    });
  }

  fetchOrganizationsRolesByAssignGroup(roleAssignGroupKey: string, searchTerm: string = '') {
    this.spinner.show("multi-org-ta");
    this.roleRequestService.getOrganizationsRolesByAssignGroup(roleAssignGroupKey, searchTerm, (this.hasProfileAdmin || roleAssignGroupKey == '1' || this.pageType == 'add-role') ? 0: this.initialOrg?.orgKey).subscribe((organizations: Organization[]) => {
      this.organizations = organizations;
      if(!this.hasProfileAdmin && (roleAssignGroupKey == '2' || roleAssignGroupKey == '3')) {
        if(this.initialOrg && !this.organizations.find(x => x.orgKey === this.initialOrg.orgKey) && this.initialOrg.irn !== '050765' /*DEW*/) {
          this.organizations.push(this.initialOrg);
        }
      }
      this.selectedOrganization = this.organizations.find(x => x.orgKey == this.roleRequestService.multiOrgFilters?.selectedOrganization?.orgKey);
      if(roleAssignGroupKey === '1') {
        this.selectedOrganization = this.organizations[0];
      }
      this.onChangeOrg();
      this.spinner.hide("multi-org-ta")
      this.updateRoleGroupVisibility();
    }, err => {
      this.spinner.hide("multi-org-ta")
    });
  }
  
  onChangeRoles() {
    this.selectedOrganization = null;
    this.updateFilters();
    this.fetchOrganizationsRolesByAssignGroup(this.selectedRoleGroup.roleAssignGroupKey);
    this.onRoleGroupChange.emit(this.selectedRoleGroup);
  }
  
  onChangeOrg() {
    this.onOrgChange.emit(this.selectedOrganization);
    this.updateFilters();
  }

  updateFilters() {
    this.roleRequestService.multiOrgFilters = {
      selectedOrganization: this.selectedOrganization,
      selectedRoleGroup: this.selectedRoleGroup,
      hasNoOrgs: this.organizations.length === 0,
      hasProfileAdmin: this.hasProfileAdmin,
    };
  }

  private updateRoleGroupVisibility() {
    this.showOrg = this.selectedRoleGroup?.roleAssignGroupKey !== '4';
    this.orgDisable = this.selectedRoleGroup?.roleAssignGroupKey === '1';
  }

  getRoleGroups() {
    this.roleRequestService.getRoleGroups().subscribe(orgRoles => {
      const defaultValues = this.roleRequestService.multiOrgFilters;
      const hasDewRole = this.roleRequestService.multiOrgFilters.userRoles.some(role => role.roleKey === 1100295 /*employee*/ 
        || role.roleKey === 1100296 /* CONTRACTOR */ 
        || role.roleKey === 1248771 /*User profile admin*/
        //|| role.roleKey === 1234856 /*OEDSR ORG ADMIN*/
        //|| role.roleKey === 1100167 /*OEDS-R Organization Administrator*/
        );

      this.roleGroups = orgRoles.filter(x => defaultValues.isDewOrg && hasDewRole || x.roleAssignGroupKey != '1');
    
      // else {
      //   this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == defaultValues?.selectedRoleGroup?.roleAssignGroupKey);
      // }
      if(defaultValues.selectedOrganization?.irn){
        if(defaultValues.selectedOrganization?.irn == '050765') {
          this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == '1');
        } else if(defaultValues.selectedOrganization?.orgTypeKeys?.includes(27)) {
          this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == '2');
        } else if(defaultValues.selectedOrganization?.orgTypeKeys?.length) {
          this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == '3');
        } else if(!defaultValues.selectedOrganization?.orgTypeKeys?.length) {
          this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == '4'); 
        }
      } else {
        this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == '4'); 
      }

      if(defaultValues.selectedOrganization?.irn == '050765' && !(defaultValues.isDewOrg && hasDewRole)) {
        this.selectedRoleGroup = this.roleGroups.find(x => x.roleAssignGroupKey == '4'); 
      }

      if(this.selectedRoleGroup){
        this.initialOrg = defaultValues.selectedOrganization ? {...defaultValues.selectedOrganization}: null;
        this.fetchOrganizationsRolesByAssignGroup(this.selectedRoleGroup.roleAssignGroupKey, defaultValues.selectedOrganization?.orgName || '');
      }

    });
  }

  // Custom filter for organization search
  customFilter = (search: string, item: Organization) => {
    return item.orgName?.toLowerCase()?.includes(search?.toLowerCase()) || item?.['orgIrn']?.toLowerCase()?.startsWith(search.toLowerCase());
  };

  // Dynamically set width for the select fields
  selectFieldsWidth(): string {
    return (this.showSelectRoles() && this.showOrg) ? '100%' : '50%';
  }

  onKeyUp(event: any) {
    this.searchSubject.next(event.target.value);
  }
}
